// pages/PaymentTrackingPage.js - Public page with your exact design
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService, {
    errorHelpers,
    formatHelpers,
    authHelpers
} from '../utils/apiService';

const PaymentTrackingPage = () => {
    // ── Component State ───────────────────────────────────────────────────────────
    const [step, setStep] = useState('input'); // 'input' | 'result' | 'loading'
    const [transactionId, setTransactionId] = useState('');
    const [trackingResult, setTrackingResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [alert, setAlert] = useState(null);
    const [verificationSettings, setVerificationSettings] = useState(null);

    const currentUser = authHelpers.getCurrentUser();

    // ── Fetch verification settings on mount ─────────────────────────
    useEffect(() => {
        loadVerificationSettings();
    }, []);

    const loadVerificationSettings = async () => {
        try {
            const response = await apiService.verification.getSettings();
            setVerificationSettings(response.data);
        } catch (error) {
            console.error('Failed to load verification settings:', error);
        }
    };

    // ── Handlers ─────────────────────────────────────────────────────────
    const handleTransactionIdChange = (e) => {
        let value = e.target.value.toUpperCase();
        // Remove any non-alphanumeric characters except dashes and underscores
        value = value.replace(/[^A-Z0-9\-_]/g, '');
        setTransactionId(value);
        setAlert(null);
    };

    const handleTrackPayment = async () => {
        if (!transactionId.trim()) {
            setAlert({ type: 'error', message: 'Please enter a transaction ID.' });
            return;
        }

        setLoading(true);
        setAlert(null);
        setStep('loading');

        try {
            // Use public endpoint for payment tracking
            const response = await apiService.verification.checkPublicPaymentStatus(transactionId);
            setTrackingResult(response.data);
            setStep('result');
        } catch (error) {
            console.error('Payment tracking error:', error);

            const errorMessage = errorHelpers.getErrorMessage(error);
            const errorCode = errorHelpers.getErrorCode(error);

            if (errorCode === 'TRANSACTION_NOT_FOUND') {
                setAlert({
                    type: 'error',
                    message: 'Transaction not found. Please check the transaction ID and try again.'
                });
            } else if (errorCode === 'STATUS_CHECK_FAILED') {
                setAlert({
                    type: 'error',
                    message: 'Unable to check payment status. Please try again later.'
                });
            } else {
                setAlert({ type: 'error', message: errorMessage });
            }

            setStep('input');
        } finally {
            setLoading(false);
        }
    };

    const handleRefreshStatus = async () => {
        if (!transactionId) return;

        setLoading(true);
        setAlert(null);

        try {
            const response = await apiService.verification.checkPublicPaymentStatus(transactionId);
            setTrackingResult(response.data);

            setAlert({
                type: 'success',
                message: 'Payment status refreshed successfully.'
            });
        } catch (error) {
            const errorMessage = errorHelpers.getErrorMessage(error);
            setAlert({ type: 'error', message: errorMessage });
        } finally {
            setLoading(false);
        }
    };

    // ── Format transaction ID for display ────────────────────────────────
    const formatTransactionIdForDisplay = (txId) => {
        if (!txId) return '';
        // Add dashes every 4 characters for better readability
        return txId.replace(/(.{4})/g, '$1-').slice(0, -1);
    };

    // ── Get status color and icon ─────────────────────────────────────────
    const getStatusDisplay = (status) => {
        const statusMap = {
            'paid': { color: 'green', icon: '✅', label: 'Payment Successful' },
            'pending': { color: 'orange', icon: '⏳', label: 'Payment Pending' },
            'failed': { color: 'red', icon: '❌', label: 'Payment Failed' }
        };
        return statusMap[status] || { color: 'gray', icon: '❓', label: 'Unknown Status' };
    };

    // ── Rendering Steps (using your exact design) ────────────────────────────────

    const renderInputStep = () => (
        <div className="tracking-step">
            <div className="step-header">
                <div className="step-icon">💳</div>
                <h2>Track Payment Status</h2>
                <p>Enter your transaction ID to check the status of your payment</p>
            </div>

            <div className="form-group">
                <label htmlFor="transaction-input" className="form-label">
                    Transaction ID <span className="required">*</span>
                </label>
                <div className="input-group">
                    <span className="input-prefix">🔗</span>
                    <input
                        id="transaction-input"
                        type="text"
                        value={transactionId}
                        onChange={handleTransactionIdChange}
                        className="form-input"
                        placeholder="e.g., CVF123456789012"
                        disabled={loading}
                        maxLength={50}
                    />
                </div>
                <div className="input-help">
                    Enter the transaction ID you received when initiating payment
                </div>
            </div>

            {verificationSettings && (
                <div className="info-card">
                    <div className="info-header">
                        <span className="info-icon">ℹ️</span>
                        <h4>Payment Information</h4>
                    </div>
                    <div className="info-grid">
                        <div className="info-item">
                            <span className="info-label">Standard Fee</span>
                            <span className="info-value">
                                {formatHelpers.formatCurrency(
                                    verificationSettings.verification_fee,
                                    verificationSettings.currency
                                )}
                            </span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Payment Timeout</span>
                            <span className="info-value">30 minutes</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Status Updates</span>
                            <span className="info-value">Real-time</span>
                        </div>
                        <div className="info-item">
                            <span className="info-label">Support</span>
                            <span className="info-value">24/7 Available</span>
                        </div>
                    </div>
                </div>
            )}

            <button
                onClick={handleTrackPayment}
                className="btn btn-primary btn-large"
                disabled={loading || !transactionId.trim()}
            >
                {loading ? (
                    <>
                        <span className="spinner"></span>
                        Checking Status...
                    </>
                ) : (
                    <>
                        <span className="btn-icon">🔍</span>
                        Track Payment
                    </>
                )}
            </button>

            <div className="help-section">
                <h4>Can't find your transaction ID?</h4>
                <ul className="help-list">
                    <li>Check your email for payment confirmation</li>
                    <li>Look in your browser downloads for payment receipts</li>
                    <li>Contact support if you need assistance</li>
                    <li>Transaction IDs usually start with 'CVF' for certificate verification</li>
                </ul>
            </div>
        </div>
    );

    const renderLoadingStep = () => (
        <div className="tracking-step">
            <div className="step-header">
                <div className="step-icon animated">⏳</div>
                <h2>Checking Payment Status</h2>
                <p>Please wait while we retrieve your payment information</p>
            </div>

            <div className="loading-content">
                <div className="progress-indicator">
                    <div className="progress-step active">
                        <span className="progress-icon">🔍</span>
                        <span className="progress-label">Searching Transaction</span>
                    </div>
                    <div className="progress-step">
                        <span className="progress-icon">🏦</span>
                        <span className="progress-label">Verifying with Bank</span>
                    </div>
                    <div className="progress-step">
                        <span className="progress-icon">📄</span>
                        <span className="progress-label">Retrieving Status</span>
                    </div>
                </div>

                <div className="transaction-info">
                    <span className="transaction-label">Transaction ID</span>
                    <span className="transaction-id">{formatTransactionIdForDisplay(transactionId)}</span>
                </div>
            </div>
        </div>
    );

    const renderResultStep = () => {
        if (!trackingResult) return null;

        const statusDisplay = getStatusDisplay(trackingResult.status);

        return (
            <div className="tracking-step">
                <div className={`step-header ${trackingResult.status}`}>
                    <div className="step-icon">{statusDisplay.icon}</div>
                    <h2>{statusDisplay.label}</h2>
                    <p>Here's the current status of your payment</p>
                </div>

                <div className={`result-card ${trackingResult.status}`}>
                    <div className="result-header">
                        <h3>Payment Details</h3>
                        <button
                            onClick={handleRefreshStatus}
                            className="btn btn-outline btn-sm"
                            disabled={loading}
                            title="Refresh payment status"
                        >
                            {loading ? (
                                <span className="spinner-sm"></span>
                            ) : (
                                <span className="btn-icon">🔄</span>
                            )}
                            Refresh
                        </button>
                    </div>

                    <div className="result-grid">
                        <div className="result-item">
                            <span className="result-label">Transaction ID</span>
                            <span className="result-value">{formatTransactionIdForDisplay(transactionId)}</span>
                        </div>

                        <div className="result-item">
                            <span className="result-label">Payment Status</span>
                            <span className={`result-value status-${trackingResult.status}`}>
                                {statusDisplay.icon} {formatHelpers.formatPaymentStatus(trackingResult.status)}
                            </span>
                        </div>

                        {trackingResult.payment_reference && (
                            <div className="result-item">
                                <span className="result-label">Payment Reference</span>
                                <span className="result-value">{trackingResult.payment_reference}</span>
                            </div>
                        )}

                        {trackingResult.amount_paid !== undefined && (
                            <div className="result-item">
                                <span className="result-label">Amount Paid</span>
                                <span className="result-value">
                                    {trackingResult.amount_paid > 0
                                        ? formatHelpers.formatCurrency(trackingResult.amount_paid, trackingResult.currency || 'NGN')
                                        : 'N/A'
                                    }
                                </span>
                            </div>
                        )}

                        {trackingResult.certificate_number && (
                            <div className="result-item">
                                <span className="result-label">Certificate Number</span>
                                <span className="result-value">{trackingResult.certificate_number}</span>
                            </div>
                        )}

                        {trackingResult.bpms_status && (
                            <div className="result-item">
                                <span className="result-label">Gateway Status</span>
                                <span className="result-value">{trackingResult.bpms_status}</span>
                            </div>
                        )}
                    </div>

                    {/* Status-specific content */}
                    {trackingResult.status === 'paid' && trackingResult.verification_status === 'success' && trackingResult.certificate && (
                        <div className="certificate-details">
                            <h4>📜 Certificate Information</h4>
                            <div className="certificate-grid">
                                <div className="cert-item">
                                    <span className="cert-label">Student Name</span>
                                    <span className="cert-value">{trackingResult.certificate.student_name}</span>
                                </div>
                                <div className="cert-item">
                                    <span className="cert-label">Course</span>
                                    <span className="cert-value">{trackingResult.certificate.course}</span>
                                </div>
                                <div className="cert-item">
                                    <span className="cert-label">Graduation Date</span>
                                    <span className="cert-value">{trackingResult.certificate.graduation_date}</span>
                                </div>
                                <div className="cert-item">
                                    <span className="cert-label">Grade</span>
                                    <span className="cert-value">{trackingResult.certificate.grade}</span>
                                </div>
                            </div>
                        </div>
                    )}

                    {trackingResult.status === 'paid' && trackingResult.verification_status === 'not_found' && (
                        <div className="status-message warning">
                            <span className="status-icon">⚠️</span>
                            <div>
                                <strong>Payment Completed</strong>
                                <p>Your payment was successful, but the certificate was not found in our database. Please contact support for assistance.</p>
                            </div>
                        </div>
                    )}

                    {trackingResult.status === 'failed' && (
                        <div className="status-message error">
                            <span className="status-icon">❌</span>
                            <div>
                                <strong>Payment Failed</strong>
                                <p>{trackingResult.message || 'Your payment could not be processed. You can try again or contact support.'}</p>
                            </div>
                        </div>
                    )}

                    {trackingResult.status === 'pending' && (
                        <div className="status-message info">
                            <span className="status-icon">⏳</span>
                            <div>
                                <strong>Payment Pending</strong>
                                <p>{trackingResult.message || 'Your payment is being processed. This usually takes a few minutes.'}</p>
                            </div>
                        </div>
                    )}
                </div>

                {/* Action buttons */}
                <div className="action-buttons">
                    <button
                        onClick={() => {
                            setTransactionId('');
                            setTrackingResult(null);
                            setStep('input');
                            setAlert(null);
                        }}
                        className="btn btn-secondary"
                    >
                        <span className="btn-icon">🔍</span>
                        Track Another Payment
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
            {/* Header 
            <header className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <div className="flex items-center">
                            <Link to="/" className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
                                    <span className="text-white font-bold text-sm">OU</span>
                                </div>
                                <span className="text-xl font-bold text-gray-900">OOU CVerify</span>
                            </Link>
                        </div>
                        <nav className="flex items-center space-x-6">
                            <Link to="/" className="text-gray-600 hover:text-gray-900 transition-colors">
                                Home
                            </Link>
                            {currentUser ? (
                                <Link
                                    to="/dashboard"
                                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    Dashboard
                                </Link>
                            ) : (
                                <Link
                                    to="/"
                                    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                                >
                                    Sign In
                                </Link>
                            )}
                        </nav>
                    </div>
                </div>
            </header>*/}

            {/* Main Content */}
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                {/* Page Header */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">
                        Track Your Payment
                    </h1>
                    <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                        Enter your transaction ID to check the status of your payment
                    </p>
                    <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-2xl mx-auto">
                        <div className="flex items-center justify-center space-x-2 text-blue-800">
                            <span className="text-lg">🌐</span>
                            <span className="font-medium">No account required - track any payment instantly</span>
                        </div>
                    </div>
                </div>

                {/* Alert */}
                {alert && (
                    <div className={`alert alert-${alert.type}`} style={{ maxWidth: '800px', margin: '0 auto 2rem' }}>
                        <span className="alert-icon">
                            {alert.type === 'error' && '⚠️'}
                            {alert.type === 'success' && '✅'}
                            {alert.type === 'info' && 'ℹ️'}
                            {alert.type === 'warning' && '⚠️'}
                        </span>
                        <span className="alert-message">{alert.message}</span>
                        <button
                            onClick={() => setAlert(null)}
                            className="alert-close"
                        >
                            ×
                        </button>
                    </div>
                )}

                {/* Use your exact tracking form design */}
                <div className="tracking-form-container">
                    <div className="tracking-form">
                        {step === 'input' && renderInputStep()}
                        {step === 'loading' && renderLoadingStep()}
                        {step === 'result' && renderResultStep()}
                    </div>
                </div>

                {/* Footer Links */}
                <div className="text-center mt-12 space-y-4">
                    <div className="flex justify-center space-x-6 text-sm text-gray-600">
                        <Link to="/" className="hover:text-gray-900 transition-colors">
                            🏠 Home
                        </Link>
                        <span>•</span>
                        <a href="mailto:support@oouagoiwoye.edu.ng" className="hover:text-gray-900 transition-colors">
                            📧 Contact Support
                        </a>
                        <span>•</span>
                        <Link to="/help" className="hover:text-gray-900 transition-colors">
                            ❓ Help Center
                        </Link>
                    </div>
                    <p className="text-xs text-gray-500">
                        &copy; {new Date().getFullYear()} Olabisi Onabanjo University. All rights reserved.                  </p>
                </div>
            </main>

            {/* Include your exact styles */}
            <style jsx>{`
                .tracking-form-container {
                    max-width: 800px;
                    margin: 0 auto;
                    padding: 1rem;
                }

                .alert {
                    display: flex;
                    align-items: center;
                    padding: 1rem;
                    border-radius: 12px;
                    margin-bottom: 2rem;
                    position: relative;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
                }

                .alert-error {
                    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
                    border: 1px solid #fecaca;
                    color: #dc2626;
                }

                .alert-success {
                    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
                    border: 1px solid #bbf7d0;
                    color: #16a34a;
                }

                .alert-info {
                    background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
                    border: 1px solid #93c5fd;
                    color: #2563eb;
                }

                .alert-warning {
                    background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
                    border: 1px solid #fde68a;
                    color: #d97706;
                }

                .alert-icon {
                    font-size: 1.25rem;
                    margin-right: 0.75rem;
                }

                .alert-message {
                    flex: 1;
                    font-weight: 500;
                }

                .alert-close {
                    background: none;
                    border: none;
                    font-size: 1.5rem;
                    cursor: pointer;
                    color: inherit;
                    opacity: 0.7;
                    transition: opacity 0.2s;
                }

                .alert-close:hover {
                    opacity: 1;
                }

                .tracking-form {
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
                    overflow: hidden;
                }

                .tracking-step {
                    padding: 3rem 2rem;
                    text-align: center;
                }

                .step-header {
                    margin-bottom: 2.5rem;
                }

                .step-header.paid {
                    color: #16a34a;
                }

                .step-header.failed {
                    color: #dc2626;
                }

                .step-header.pending {
                    color: #d97706;
                }

                .step-icon {
                    font-size: 4rem;
                    margin-bottom: 1rem;
                    display: block;
                }

                .step-icon.animated {
                    animation: pulse 2s infinite;
                }

                .step-header h2 {
                    font-size: 2rem;
                    font-weight: 700;
                    color: #1f2937;
                    margin-bottom: 0.75rem;
                }

                .step-header.paid h2 {
                    color: #16a34a;
                }

                .step-header.failed h2 {
                    color: #dc2626;
                }

                .step-header.pending h2 {
                    color: #d97706;
                }

                .step-header p {
                    font-size: 1.125rem;
                    color: #6b7280;
                    margin: 0;
                }

                .form-group {
                    margin-bottom: 2rem;
                    text-align: left;
                }

                .form-label {
                    display: block;
                    font-size: 1rem;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 0.5rem;
                }

                .required {
                    color: #dc2626;
                }

                .input-group {
                    position: relative;
                    display: flex;
                    align-items: center;
                }

                .input-prefix {
                    position: absolute;
                    left: 1rem;
                    font-size: 1.25rem;
                    z-index: 1;
                }

                .form-input {
                    width: 100%;
                    padding: 1rem 1rem 1rem 3rem;
                    border: 2px solid #e5e7eb;
                    border-radius: 12px;
                    font-size: 1.125rem;
                    font-weight: 500;
                    font-family: 'Courier New', monospace;
                    text-transform: uppercase;
                    transition: all 0.3s ease;
                    background: #f9fafb;
                    letter-spacing: 1px;
                }

                .form-input:focus {
                    outline: none;
                    border-color: #3b82f6;
                    background: white;
                    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
                }

                .form-input:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .input-help {
                    font-size: 0.875rem;
                    color: #6b7280;
                    margin-top: 0.5rem;
                }

                .info-card {
                    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
                    border: 1px solid #bae6fd;
                    border-radius: 16px;
                    padding: 1.5rem;
                    margin-bottom: 2rem;
                    text-align: left;
                }

                .info-header {
                    display: flex;
                    align-items: center;
                    margin-bottom: 1rem;
                }

                .info-icon {
                    font-size: 1.5rem;
                    margin-right: 0.75rem;
                }

                .info-header h4 {
                    font-size: 1.25rem;
                    font-weight: 600;
                    color: #0c4a6e;
                    margin: 0;
                }

                .info-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 1rem;
                }

                .info-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.75rem;
                    background: rgba(255, 255, 255, 0.7);
                    border-radius: 8px;
                }

                .info-label {
                    font-weight: 500;
                    color: #0c4a6e;
                }

                .info-value {
                    font-weight: 600;
                    color: #1e40af;
                }

                .help-section {
                    margin-top: 2rem;
                    text-align: left;
                }

                .help-section h4 {
                    font-size: 1.125rem;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 1rem;
                }

                .help-list {
                    list-style: none;
                    padding: 0;
                    margin: 0;
                }

                .help-list li {
                    padding: 0.5rem 0;
                    color: #6b7280;
                    position: relative;
                    padding-left: 1.5rem;
                }

                .help-list li::before {
                    content: '•';
                    position: absolute;
                    left: 0;
                    color: #3b82f6;
                    font-weight: bold;
                }

                .loading-content {
                    max-width: 500px;
                    margin: 0 auto;
                }

                .progress-indicator {
                    display: flex;
                    justify-content: space-between;
                    margin-bottom: 2rem;
                    position: relative;
                }

                .progress-indicator::before {
                    content: '';
                    position: absolute;
                    top: 1.5rem;
                    left: 2rem;
                    right: 2rem;
                    height: 2px;
                    background: #e5e7eb;
                    z-index: 0;
                }

                .progress-step {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    position: relative;
                    z-index: 1;
                }

                .progress-icon {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 3rem;
                    height: 3rem;
                    border-radius: 50%;
                    background: #f3f4f6;
                    color: #9ca3af;
                    font-weight: 600;
                    margin-bottom: 0.5rem;
                }

                .progress-step.active .progress-icon {
                    background: #3b82f6;
                    color: white;
                    animation: pulse 2s infinite;
                }

                .progress-label {
                    font-size: 0.875rem;
                    color: #6b7280;
                    text-align: center;
                }

                .progress-step.active .progress-label {
                    color: #3b82f6;
                    font-weight: 500;
                }

                .transaction-info {
                    background: #f8fafc;
                    border: 1px solid #e2e8f0;
                    border-radius: 12px;
                    padding: 1rem;
                    margin-bottom: 2rem;
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .transaction-label {
                    font-weight: 500;
                    color: #475569;
                }

                .transaction-id {
                    font-family: 'Courier New', monospace;
                    font-weight: 600;
                    color: #1e293b;
                    letter-spacing: 1px;
                }

                .result-card {
                    border-radius: 16px;
                    padding: 2rem;
                    margin-bottom: 2rem;
                    text-align: left;
                }

                .result-card.paid {
                    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%);
                    border: 2px solid #bbf7d0;
                }

                .result-card.failed {
                    background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
                    border: 2px solid #fecaca;
                }

                .result-card.pending {
                    background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
                    border: 2px solid #fde68a;
                }

                .result-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 1.5rem;
                }

                .result-header h3 {
                    font-size: 1.5rem;
                    font-weight: 600;
                    color: #1f2937;
                    margin: 0;
                }

                .result-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
                    gap: 1rem;
                    margin-bottom: 1.5rem;
                }

                .result-item {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 1rem;
                    background: rgba(255, 255, 255, 0.8);
                    border-radius: 12px;
                }

                .result-label {
                    font-weight: 500;
                    color: #374151;
                }

                .result-value {
                    font-weight: 600;
                    color: #1f2937;
                    text-align: right;
                    font-family: 'Courier New', monospace;
                }

                .result-value.status-paid {
                    color: #16a34a;
                }

                .result-value.status-failed {
                    color: #dc2626;
                }

                .result-value.status-pending {
                    color: #d97706;
                }

                .certificate-details {
                    margin-top: 1.5rem;
                    padding: 1rem;
                    background: rgba(255, 255, 255, 0.6);
                    border-radius: 12px;
                }

                .certificate-details h4 {
                    font-size: 1.125rem;
                    font-weight: 600;
                    color: #374151;
                    margin-bottom: 1rem;
                }

                .certificate-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 0.75rem;
                }

                .cert-item {
                    display: flex;
                    flex-direction: column;
                    gap: 0.25rem;
                }

                .cert-label {
                    font-size: 0.875rem;
                    font-weight: 500;
                    color: #6b7280;
                }

                .cert-value {
                    font-weight: 600;
                    color: #1f2937;
                }

                .status-message {
                    display: flex;
                    align-items: flex-start;
                    gap: 0.75rem;
                    padding: 1rem;
                    border-radius: 12px;
                    margin-top: 1rem;
                }

                .status-message.info {
                    background: rgba(59, 130, 246, 0.1);
                    border: 1px solid rgba(59, 130, 246, 0.2);
                }

                .status-message.warning {
                    background: rgba(217, 119, 6, 0.1);
                    border: 1px solid rgba(217, 119, 6, 0.2);
                }

                .status-message.error {
                    background: rgba(220, 38, 38, 0.1);
                    border: 1px solid rgba(220, 38, 38, 0.2);
                }

                .status-icon {
                    font-size: 1.25rem;
                    flex-shrink: 0;
                    margin-top: 0.125rem;
                }

                .status-message strong {
                    display: block;
                    margin-bottom: 0.25rem;
                }

                .status-message p {
                    margin: 0;
                    color: #6b7280;
                }

                .action-buttons {
                    display: flex;
                    gap: 1rem;
                    margin-top: 2rem;
                    flex-wrap: wrap;
                }

                .btn {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    padding: 0.75rem 1.5rem;
                    border: none;
                    border-radius: 12px;
                    font-size: 1rem;
                    font-weight: 600;
                    text-decoration: none;
                    cursor: pointer;
                    transition: all 0.3s ease;
                    min-height: 48px;
                }

                .btn:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }

                .btn-large {
                    padding: 1rem 2rem;
                    font-size: 1.125rem;
                    min-height: 56px;
                }

                .btn-sm {
                    padding: 0.5rem 1rem;
                    font-size: 0.875rem;
                    min-height: 40px;
                }

                .btn-primary {
                    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
                    color: white;
                    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
                }

                .btn-primary:hover:not(:disabled) {
                    background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
                    transform: translateY(-2px);
                    box-shadow: 0 6px 16px rgba(59, 130, 246, 0.4);
                }

                .btn-secondary {
                    background: #f8fafc;
                    color: #475569;
                    border: 2px solid #e2e8f0;
                }

                .btn-secondary:hover:not(:disabled) {
                    background: #f1f5f9;
                    border-color: #cbd5e1;
                }

                .btn-outline {
                    background: transparent;
                    color: #3b82f6;
                    border: 2px solid #3b82f6;
                }

                .btn-outline:hover:not(:disabled) {
                    background: #3b82f6;
                    color: white;
                }

                .btn-icon {
                    font-size: 1.125rem;
                }

                .spinner {
                    display: inline-block;
                    width: 1rem;
                    height: 1rem;
                    border: 2px solid rgba(255, 255, 255, 0.3);
                    border-top: 2px solid currentColor;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                .spinner-sm {
                    display: inline-block;
                    width: 0.875rem;
                    height: 0.875rem;
                    border: 2px solid rgba(59, 130, 246, 0.3);
                    border-top: 2px solid #3b82f6;
                    border-radius: 50%;
                    animation: spin 1s linear infinite;
                }

                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }

                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.5; }
                }

                @media (max-width: 768px) {
                    .tracking-step {
                        padding: 2rem 1rem;
                    }

                    .step-header h2 {
                        font-size: 1.5rem;
                    }

                    .step-header p {
                        font-size: 1rem;
                    }

                    .info-grid {
                        grid-template-columns: 1fr;
                    }

                    .result-grid {
                        grid-template-columns: 1fr;
                    }

                    .certificate-grid {
                        grid-template-columns: 1fr;
                    }

                    .action-buttons {
                        flex-direction: column;
                    }

                    .result-header {
                        flex-direction: column;
                        gap: 1rem;
                        align-items: stretch;
                    }

                    .progress-indicator {
                        flex-direction: column;
                        gap: 1rem;
                    }

                    .progress-indicator::before {
                        display: none;
                    }

                    .progress-step {
                        flex-direction: row;
                        text-align: left;
                    }

                    .progress-icon {
                        margin-right: 1rem;
                        margin-bottom: 0;
                    }
                }

                /* Additional responsive styles for the header */
                .min-h-screen {
                    min-height: 100vh;
                }

                /* Focus states for accessibility */
                .btn:focus,
                .form-input:focus {
                    outline: 2px solid #3b82f6;
                    outline-offset: 2px;
                }

                /* High contrast mode support */
                @media (prefers-contrast: high) {
                    .result-card {
                        border-width: 3px;
                    }
                    
                    .btn {
                        border: 2px solid currentColor;
                    }
                    
                    .form-input {
                        border-width: 3px;
                    }
                }
            `}</style>
        </div>
    );
};

export default PaymentTrackingPage;